// src/pages/Production/ProfitabilityDetail.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, onSnapshot, query, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
    },
    summaryCard: {
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0'
    },
    cardTitle: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '6px',
        fontWeight: '500'
    },
    cardValue: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#0f172a'
    },
    profitCard: {
        background: 'linear-gradient(135deg, #059669, #10b981)',
        color: 'white',
        border: 'none'
    },
    lossCard: {
        background: 'linear-gradient(135deg, #dc2626, #ef4444)',
        color: 'white',
        border: 'none'
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    costBreakdown: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '15px'
    },
    costItem: {
        background: '#f8fafc',
        padding: '16px',
        borderRadius: '10px',
        border: '1px solid #e2e8f0'
    },
    costLabel: {
        fontSize: '12px',
        color: '#64748b',
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    costValue: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    statusBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    progressContainer: {
        marginTop: '20px'
    },
    progressBar: {
        height: '10px',
        background: '#e2e8f0',
        borderRadius: '5px',
        overflow: 'hidden',
        marginTop: '8px'
    },
    progressFill: {
        height: '100%',
        borderRadius: '5px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: '#64748b'
    }
};

function ProfitabilityDetail() {
    const { productId } = useParams();
    const { userData } = useAuth();
    const [product, setProduct] = useState(null);
    const [orders, setOrders] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch data
    useEffect(() => {
        if (!userData?.companyId || !productId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch this product
        const fetchProduct = async () => {
            const productDoc = await getDoc(doc(db, 'companies', companyId, 'products', productId));
            if (productDoc.exists()) {
                setProduct({ id: productDoc.id, ...productDoc.data() });
            }
        };
        fetchProduct();

        // Fetch all products
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch orders
        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'orders'), orderBy('orderDate', 'desc')),
            snap => setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch raw materials
        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch purchases
        const unsubPurchases = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterialPurchases')),
            snap => setRawMaterialPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch subcontractors
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => {
                setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubProducts();
            unsubOrders();
            unsubRecipes();
            unsubRawMaterials();
            unsubPurchases();
            unsubSubcontractors();
        };
    }, [userData, productId]);

    // Get purchase price helper
    const getMaterialPurchasePrice = (materialId, materialName) => {
        const purchasesWithMaterial = rawMaterialPurchases
            .filter(p => p.lineItems && p.lineItems.some(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            ))
            .sort((a, b) => {
                const dateA = a.purchaseDate?.toDate?.() || (a.purchaseDate?.seconds ? new Date(a.purchaseDate.seconds * 1000) : new Date(0));
                const dateB = b.purchaseDate?.toDate?.() || (b.purchaseDate?.seconds ? new Date(b.purchaseDate.seconds * 1000) : new Date(0));
                return dateB - dateA;
            });

        if (purchasesWithMaterial.length > 0) {
            const lineItem = purchasesWithMaterial[0].lineItems.find(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            );
            if (lineItem && lineItem.price > 0) return lineItem.price;
        }
        return null;
    };

    // Calculate profitability
    const profitabilityData = useMemo(() => {
        if (!product) return null;

        // All materials
        const allMaterials = [
            ...rawMaterials.map(rm => ({
                id: rm.id,
                name: rm.name,
                unitPrice: getMaterialPurchasePrice(rm.id, rm.name) || rm.unitPrice || 0
            })),
            ...allProducts.filter(p => p.isRawMaterial).map(p => ({
                id: p.id,
                name: p.name,
                unitPrice: getMaterialPurchasePrice(p.id, p.name) || p.purchasePrice || 0
            }))
        ];

        // Product sales
        const productSales = [];
        let totalRevenue = 0;
        let totalQuantitySold = 0;

        orders.forEach(order => {
            if (order.lineItems) {
                order.lineItems.forEach(item => {
                    if (item.productId === product.id || item.name === product.name) {
                        const orderDate = order.orderDate?.toDate?.() ||
                            (order.orderDate?.seconds ? new Date(order.orderDate.seconds * 1000) : new Date());

                        productSales.push({
                            orderId: order.id,
                            orderNo: order.id.substring(0, 8).toUpperCase(),
                            customerName: order.customerName || 'Bilinmeyen',
                            date: orderDate,
                            quantity: item.quantity || 0,
                            unitPrice: item.price || 0,
                            total: item.total || (item.price * item.quantity) || 0,
                            status: order.status || 'Beklemede',
                            paymentStatus: order.paymentStatus || 'pending'
                        });
                        totalRevenue += item.total || (item.price * item.quantity) || 0;
                        totalQuantitySold += item.quantity || 0;
                    }
                });
            }
        });

        // Recipe costs
        const recipe = recipes.find(r => r.product === product.name || r.productId === product.id);
        let rawMaterialCost = 0;
        let subcontractingCost = 0;
        let laborCost = 0;

        if (recipe && recipe.stages) {
            recipe.stages.forEach(stage => {
                if (stage.inputMaterials) {
                    stage.inputMaterials.forEach(material => {
                        const rawMat = allMaterials.find(rm => rm.name === material.material);
                        if (rawMat) {
                            rawMaterialCost += (rawMat.unitPrice || 0) * (material.quantity || 0);
                        }
                    });
                }
                if (stage.type === 'subcontracting' && stage.subcontractors) {
                    stage.subcontractors.forEach(sub => {
                        const supplier = subcontractors.find(s => s.id === sub.supplierId);
                        if (supplier?.costTariff?.[0]) {
                            subcontractingCost += supplier.costTariff[0].value || 0;
                        }
                        laborCost += sub.laborCost || 0;
                    });
                }
                if (stage.type === 'internal' && stage.duration) {
                    laborCost += (stage.duration / 60) * 15;
                }
            });
        }

        const overheadCost = (rawMaterialCost + laborCost) * 0.10;
        const unitCost = rawMaterialCost + subcontractingCost + laborCost + overheadCost;
        const totalCost = unitCost * totalQuantitySold;
        const grossProfit = totalRevenue - totalCost;
        const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

        return {
            sales: productSales.sort((a, b) => b.date - a.date),
            totalRevenue,
            totalQuantitySold,
            unitCost,
            totalCost,
            grossProfit,
            profitMargin,
            hasRecipe: !!recipe,
            costBreakdown: {
                rawMaterials: rawMaterialCost,
                subcontracting: subcontractingCost,
                labor: laborCost,
                overhead: overheadCost
            }
        };
    }, [product, orders, recipes, rawMaterials, rawMaterialPurchases, subcontractors, allProducts]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getStatusStyle = (status) => {
        const statusStyles = {
            'Tamamlandı': { background: '#dcfce7', color: '#166534' },
            'Hazırlanıyor': { background: '#dbeafe', color: '#1d4ed8' },
            'Beklemede': { background: '#fef3c7', color: '#92400e' },
            'İptal': { background: '#fef2f2', color: '#dc2626' }
        };
        return statusStyles[status] || { background: '#f1f5f9', color: '#64748b' };
    };

    const getPaymentStatusStyle = (status) => {
        const styles = {
            'paid': { background: '#dcfce7', color: '#166534', text: 'Ödendi' },
            'partial': { background: '#dbeafe', color: '#1d4ed8', text: 'Kısmi' },
            'pending': { background: '#fef2f2', color: '#dc2626', text: 'Bekliyor' }
        };
        return styles[status] || styles['pending'];
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                        <p>Karlılık detayları yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!product || !profitabilityData) {
        return (
            <div style={styles.container}>
                <Link to="/uretim/karlilik-analizi" style={styles.backLink}>← Karlılık Analizi</Link>
                <div style={styles.emptyState}>
                    <h2>Ürün bulunamadı</h2>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/karlilik-analizi" style={styles.backLink}>← Karlılık Analizi</Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>{product.name}</h2>
                <p style={{ color: '#64748b', marginTop: '8px' }}>
                    {product.barcode && `Kod: ${product.barcode} | `}
                    {profitabilityData.totalQuantitySold} adet satış | {profitabilityData.sales.length} sipariş
                </p>

                {/* Summary Cards */}
                <div style={{ ...styles.summaryCards, marginTop: '30px' }}>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>📈 Toplam Gelir</div>
                        <div style={styles.cardValue}>{formatCurrency(profitabilityData.totalRevenue)}</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>📦 Birim Maliyet</div>
                        <div style={styles.cardValue}>{formatCurrency(profitabilityData.unitCost)}</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>📉 Toplam Maliyet</div>
                        <div style={styles.cardValue}>{formatCurrency(profitabilityData.totalCost)}</div>
                    </div>
                    <div style={{
                        ...styles.summaryCard,
                        ...(profitabilityData.grossProfit >= 0 ? styles.profitCard : styles.lossCard)
                    }}>
                        <div style={{ ...styles.cardTitle, color: 'rgba(255,255,255,0.9)' }}>
                            {profitabilityData.grossProfit >= 0 ? '💰 Brüt Kâr' : '⚠️ Zarar'}
                        </div>
                        <div style={{ ...styles.cardValue, color: 'white' }}>
                            {formatCurrency(Math.abs(profitabilityData.grossProfit))}
                        </div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>📊 Kâr Marjı</div>
                        <div style={{
                            ...styles.cardValue,
                            color: profitabilityData.profitMargin >= 0 ? '#059669' : '#dc2626'
                        }}>
                            %{profitabilityData.profitMargin.toFixed(1)}
                        </div>
                    </div>
                </div>

                {/* Cost Breakdown */}
                <div style={{ marginTop: '30px' }}>
                    <h3 style={styles.sectionTitle}>💵 Maliyet Dağılımı (Birim)</h3>
                    <div style={styles.costBreakdown}>
                        <div style={styles.costItem}>
                            <div style={styles.costLabel}>Hammadde</div>
                            <div style={styles.costValue}>{formatCurrency(profitabilityData.costBreakdown.rawMaterials)}</div>
                        </div>
                        <div style={styles.costItem}>
                            <div style={styles.costLabel}>Fason İşçilik</div>
                            <div style={styles.costValue}>{formatCurrency(profitabilityData.costBreakdown.subcontracting)}</div>
                        </div>
                        <div style={styles.costItem}>
                            <div style={styles.costLabel}>İşçilik</div>
                            <div style={styles.costValue}>{formatCurrency(profitabilityData.costBreakdown.labor)}</div>
                        </div>
                        <div style={styles.costItem}>
                            <div style={styles.costLabel}>Genel Gider</div>
                            <div style={styles.costValue}>{formatCurrency(profitabilityData.costBreakdown.overhead)}</div>
                        </div>
                    </div>
                    {!profitabilityData.hasRecipe && (
                        <p style={{ color: '#f59e0b', marginTop: '15px', fontSize: '14px' }}>
                            ⚠️ Bu ürün için reçete tanımlanmamış, maliyet tahminidir.
                        </p>
                    )}
                </div>

                {/* Sales History */}
                <div style={{ marginTop: '40px' }}>
                    <h3 style={styles.sectionTitle}>📋 Satış Geçmişi</h3>
                    {profitabilityData.sales.length === 0 ? (
                        <div style={styles.emptyState}>
                            <p>Bu ürün için henüz satış kaydı bulunmuyor.</p>
                        </div>
                    ) : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Sipariş No</th>
                                    <th style={styles.th}>Müşteri</th>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>Adet</th>
                                    <th style={styles.th}>Birim Fiyat</th>
                                    <th style={styles.th}>Toplam</th>
                                    <th style={styles.th}>Durum</th>
                                    <th style={styles.th}>Ödeme</th>
                                </tr>
                            </thead>
                            <tbody>
                                {profitabilityData.sales.slice(0, 20).map((sale, index) => (
                                    <tr key={index}>
                                        <td style={styles.td}>
                                            <strong>{sale.orderNo}</strong>
                                        </td>
                                        <td style={styles.td}>{sale.customerName}</td>
                                        <td style={styles.td}>{formatDate(sale.date)}</td>
                                        <td style={styles.td}>{sale.quantity}</td>
                                        <td style={styles.td}>{formatCurrency(sale.unitPrice)}</td>
                                        <td style={{ ...styles.td, fontWeight: '600' }}>{formatCurrency(sale.total)}</td>
                                        <td style={styles.td}>
                                            <span style={{ ...styles.statusBadge, ...getStatusStyle(sale.status) }}>
                                                {sale.status}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                ...getPaymentStatusStyle(sale.paymentStatus)
                                            }}>
                                                {getPaymentStatusStyle(sale.paymentStatus).text}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {profitabilityData.sales.length > 20 && (
                        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '15px' }}>
                            ... ve {profitabilityData.sales.length - 20} satış daha
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProfitabilityDetail;
